import React, { useEffect } from 'react'
import styled from 'styled-components'
import gsap from 'gsap'
import TransitionLink from 'gatsby-plugin-transition-link'
import data from './../data/en_new.json'
import { GhostButton } from './'

const MenuView = styled.div`
  position: fixed;
  display: flex;
  padding: 1.25rem;
  opacity: 0;
  align-items: center;
  z-index: 200;
  text-align: left;
`

const MenuTitle = styled.h3`
  margin-bottom: 1.5rem;
  font-size: 17px;
  font-weight: 100;
  margin: 0 auto 1.3rem;
  color: #00B0B9;

  @media screen and (min-width: 768px) {
    margin: 0 0 2rem;
    text-align: left;
  }
`

export const EventWrapper = styled.div`
  color: #ffffff;
  font-weight: 100;
  text-align: center;
  margin-bottom: 2rem;
  padding: 0 1rem;

  &:first-child {
    margin-top: 2rem;
  }

  @media screen and (min-width: 768px) {
    margin-bottom: 3.5rem;
    text-align: left;

    &:first-child {
      margin-top: 0;
    }
  }
`

const Date = styled.p`
  font-size: 17px;
  font-weight: 100;
`

export const EventName = styled.p`
  margin: .3rem 0 .5rem;
  font-size: 1rem;
  font-weight: 700;

  @media screen and (min-width: 768px) {
    font-size: 1.25rem;
    margin: .3rem 0 1.5rem;
  }
`

export const Location = styled.p`
  font-size: 17px;
  font-weight: 100;
  font-family: 'Sica', sans-serif;
`

export const EventView = ({ date, event, location }) => (
  <EventWrapper>
    <Date>{date}</Date>
    <EventName>{event}</EventName>
    <Location>{location}</Location>
  </EventWrapper>
)

export const LinkTitle = styled(EventName)``

const ContainerView = styled.div`
  max-width: 920px;
  margin: 0 auto;
  z-index: 201;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  a {
    display: flex;
    align-items: center;
    margin: 0 auto;

    svg {
      width: 12px;
      height: 12px;
    }
  }
  @media screen and (min-width: 768px) {
    a {
      margin: 0;
    }
  }
`

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.2rem;

  @media screen and (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem 2.5rem;
  }
`

const Close = styled.div`
  display: none;
  position: absolute;
  top: 17px;
  right: 49px;
  color: #fff;
  font-size: 12px;
  font-family: 'Sica', sans-serif;
  cursor: pointer;

  @media screen and (min-width: 768px) {
    display: flex;
  }
`

const Bg = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 100%;
  bottom: 100vh;
  background: #041E42;
  z-index: 100;
`

const ArrowView = styled.div`
  display: inline-block;
  width: ${({width}) => width || '12px'};
  height: ${({height}) => height || '12px'};
  margin-left: .6rem;
  ${({transform}) => transform}
  
  svg {
    height: 100%;
    width: 100%;
  }
`

const ArrowSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14.981" height="14.981" viewBox="0 0 14.981 14.981">
    <g transform="translate(-1263.186 -14.5)">
      <path id="Path_986" data-name="Path 986" d="M390-215h14.127v14.127" transform="translate(873.539 230)" fill="none" stroke='#ffffff'strokeMiterlimit="10" strokeWidth="1"/>
      <line id="Line_12" data-name="Line 12" x1="13.833" y2="13.833" transform="translate(1263.539 15.295)" fill="none" stroke='#ffffff' strokeMiterlimit="10" strokeWidth="1"/>
    </g>
  </svg>
)

export const Arrow = ({ height, width, transform }) => (
  <ArrowView height={height} width={width} transform={transform}>
    <ArrowSvg />
  </ArrowView>
)

const LearnMoreView = styled.div`
  display: block;
  font-size: 17px;
  color: #ffffff;
  font-weight: 100;

  @media screen and (min-width: 768px) {
    width: 100%;
  }
`

const MainEventMenu = ({ isOpen, closeMenu, openSavedEvents }) => {

  useEffect(() => {
    if(isOpen) {
      gsap.set('.eventsMenu', {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      })

      gsap.to('.eventsMenu', {
        duration: .5,
        delay: .15,
        opacity: 1,
        ease: 'power3.inOut',
      })

      gsap.to('.background', {
        duration: .5,
        left: 0,
        bottom: 0,
        ease: 'power3.inOut',
      })
    } else {

      gsap.to('.background', {
        duration: .5,
        left: '100%',
        bottom: '100%',
        ease: 'power3.inOut',
      })

      gsap.to('.eventsMenu', {
        duration: .2,
        opacity: 0,
        ease: 'power3.inOut',
        onComplete: () => gsap.set('.eventsMenu', {
          top: 0,
          left: '100%',
          right: 0,
          bottom: '100%',
        }),
      })

    }
  }, [isOpen])

  return <>
    <Bg className='background' />
    <MenuView className='eventsMenu'>
      <Close onClick={closeMenu}>
        <div>CLOSE</div>
        <Arrow transform='transform: rotate(90deg);'/>
      </Close>

      <ContainerView >
        <div style={{color: '#fff', fontSize:'3rem', margin: '0 0 3rem 0'}}>PROGRAM OVERVIEW</div>
        <MenuTitle>Main event</MenuTitle>
        <EventsGrid>
          {data.events.map((event, i) => <EventView key={`${event.location}-${i}`} {...event}/>)}
          <TransitionLink
            to="/signup"
            exit={{
              trigger: ({ entry, node }) => gsap.to(node, { xPercent: -100, duration: .4, ease: 'power3.out', }),
              length: .4
            }}
            entry={{
              trigger: ({ exit, node }) => gsap.from(node, { xPercent: 100, duration: .4, ease: 'power3.out', }),
              length: .4
            }}
          >
            <GhostButton>
              Click here to attend
            </GhostButton>
          </TransitionLink>

          <TransitionLink
            to="/petur-larsen"
            style={{ color: '#ffffff' }}
            exit={{
              trigger: ({ entry, node }) => gsap.to(node, { opacity: 0, duration: .3 }),
              length: .3
            }}
            entry={{
              trigger: ({ exit, node }) => gsap.from(node, { opacity: 0, duration: .3 }),
              delay: .3,
              length: .3
            }}
          >
            <LearnMoreView>
              <LinkTitle>Full Program</LinkTitle>
              <span>Learn more about the company anniversary events and activities, and other recommended activities and experiences that you can enjoy during your stay.</span>
              <Arrow transform='transform: rotate(45deg) translateX(4px);'/>
            </LearnMoreView>
          </TransitionLink>
        </EventsGrid>
    </ContainerView>
   </MenuView>
  </>
}

export default MainEventMenu